import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, LogOut } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ChangePassword() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  if (!user) {
    navigate("/login", { replace: true });
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("La nueva contraseña debe tener al menos 8 caracteres");
    if (next !== confirm) return toast.error("Las contraseñas no coinciden");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Contraseña actualizada");
      setUser({ ...user, must_change_password: false });
      navigate(user.role === "admin" ? "/admin" : "/portal", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-10">
          <div className="w-12 h-12 rounded-xl border border-[#D4AF37]/40 grid place-items-center mb-6">
            <KeyRound className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          {user.must_change_password ? (
            <>
              <div className="text-xs uppercase tracking-[0.28em] text-[#D4AF37] mb-3">Primer acceso</div>
              <h1 className="font-serif text-3xl text-white mb-2">Elige tu nueva contraseña</h1>
              <p className="text-zinc-400 text-sm mb-8">
                Por seguridad, antes de entrar debes reemplazar la contraseña temporal que te enviamos por email.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-serif text-3xl text-white mb-2">Cambiar contraseña</h1>
              <p className="text-zinc-400 text-sm mb-8">Introduce tu contraseña actual y la nueva.</p>
            </>
          )}

          <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
            <div>
              <label className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-2 block">
                Contraseña actual
              </label>
              <input
                required
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                data-testid="change-current"
                className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-2 block">
                Nueva contraseña
              </label>
              <input
                required
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                data-testid="change-new"
                className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
              />
              <div className="text-xs text-zinc-600 mt-1.5">Mínimo 8 caracteres.</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-2 block">Confirmar</label>
              <input
                required
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="change-confirm"
                className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="change-submit"
              className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors disabled:opacity-60"
            >
              {loading ? "Guardando…" : "Guardar contraseña"}
            </button>
          </form>

          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="mt-6 w-full text-xs text-zinc-500 hover:text-zinc-300 flex items-center justify-center gap-2"
          >
            <LogOut className="w-3.5 h-3.5" /> Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
}
